import type { FC, HTMLAttributes, ReactNode } from 'react'
import styles from './chip.module.css'

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  label?: ReactNode
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
  variant?: 'filled' | 'outlined'
  size?: 'small' | 'medium'
  icon?: ReactNode
  deleteIcon?: ReactNode
  onDelete?: () => void
  clickable?: boolean
}

const colorMap: Record<string, string> = {
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
  success: styles.colorSuccess,
  error: styles.colorError,
  warning: styles.colorWarning,
  info: styles.colorInfo,
}

export const Chip: FC<ChipProps> = ({
  label,
  color = 'default',
  variant = 'filled',
  size = 'medium',
  icon,
  deleteIcon,
  onDelete,
  clickable,
  className,
  children,
  onClick,
  ...rest
}) => {
  const classes = [
    styles.chip,
    color !== 'default' ? colorMap[color] : undefined,
    variant === 'outlined' ? styles.outlined : undefined,
    size !== 'medium' ? styles.sizeSmall : undefined,
    clickable || onClick ? styles.clickable : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span className={classes} tabIndex={clickable || onClick ? 0 : undefined} onClick={onClick} {...rest}>
      {icon}
      {label ?? children}
      {onDelete ? (
        <span
          className={styles.delete}
          role="button"
          tabIndex={0}
          aria-label="Remove"
          onClick={e => {
            e.stopPropagation()
            onDelete()
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation()
              onDelete()
            }
          }}
        >
          {deleteIcon ?? '\u00d7'}
        </span>
      ) : null}
    </span>
  )
}
